/* eslint-disable @typescript-eslint/no-shadow */
/* eslint-disable no-redeclare */
import type { InvoiceStatus, InvoiceStatusId } from '@rentguru/commons-utils';
import React, { Reducer, useReducer } from 'react';

import type { InvoiceFilterState } from 'src/components/MyCompany/Invoices/InvoiceFilters/invoiceSavedFilterUtils';
import {
  EMPTY_INVOICE_DATE_FILTER,
  InvoiceGroupByOption,
  InvoiceTableSortOptions,
  type InvoiceDateFilter,
} from 'src/components/MyCompany/Invoices/invoiceUtils';
import { OwnerStatementsSortOptions } from 'src/components/MyCompany/Owners/ownersUtils';

export interface MyCompanyContext extends MyCompanyState {
  updateSelectedOwnersStatements: (selected: string[]) => void;
  updateSortOption: (sortOption: OwnerStatementsSortOptions) => void;
  updateInvoiceSortOption: (sortOption: InvoiceTableSortOptions) => void;
  updateOwnerNameFilter: (name: string) => void;
  updateInvoicesFilter: (name: string) => void;
  updateSelectedInvoices: (selected: string[]) => void;
  toggleInvoiceStatusFilter: (status: InvoiceStatus) => void;
  toggleInvoicePaymentStatusFilter: (paymentStatusId: InvoiceStatusId) => void;
  toggleInvoiceCustomerFilter: (ownerId: string) => void;
  updateInvoiceDateFilter: (dateFilter: InvoiceDateFilter) => void;
  updateInvoiceGroupBy: (groupBy: InvoiceGroupByOption | null) => void;
  clearInvoiceFilters: () => void;
  applyInvoiceFilters: (filters: InvoiceFilterState) => void;
}

interface MyCompanyState {
  selectedOwnersStatementIds: string[];
  selectedInvoicesIds: string[];
  sortOption: OwnerStatementsSortOptions;
  invoiceSortOption: InvoiceTableSortOptions;
  ownerNameFilter: string;
  invoiceNameFilter: string;
  invoiceStatusFilter: Set<InvoiceStatus>;
  invoicePaymentStatusFilter: Set<InvoiceStatusId>;
  invoiceCustomerFilter: Set<string>;
  invoiceDateFilter: InvoiceDateFilter;
  invoiceGroupBy: InvoiceGroupByOption | null;
}

type Action =
  | {
      type: 'SET_SELECTED_OWNERS_STATEMENTS';
      payload: string[];
    }
  | {
      type: 'SET_SELECTED_INVOICES';
      payload: string[];
    }
  | {
      type: 'SET_SORT_OPTION';
      payload: OwnerStatementsSortOptions;
    }
  | {
      type: 'UPDATE_OWNER_NAME_FILTER';
      payload: string;
    }
  | {
      type: 'UPDATE_INVOICES_FILTER';
      payload: string;
    }
  | {
      type: 'SET_INVOICE_SORT_OPTION';
      payload: InvoiceTableSortOptions;
    }
  | { type: 'TOGGLE_INVOICE_STATUS_FILTER'; payload: InvoiceStatus }
  | { type: 'TOGGLE_INVOICE_PAYMENT_STATUS_FILTER'; payload: InvoiceStatusId }
  | { type: 'TOGGLE_INVOICE_CUSTOMER_FILTER'; payload: string }
  | { type: 'SET_INVOICE_DATE_FILTER'; payload: InvoiceDateFilter }
  | { type: 'SET_INVOICE_GROUP_BY'; payload: InvoiceGroupByOption | null }
  | { type: 'SET_INVOICE_FILTERS'; payload: InvoiceFilterState }
  | { type: 'CLEAR_INVOICE_FILTERS' };

const toggleInSet = <TValue,>(set: Set<TValue>, value: TValue): Set<TValue> => {
  const next = new Set(set);
  if (next.has(value)) {
    next.delete(value);
  } else {
    next.add(value);
  }
  return next;
};

const initialState: MyCompanyState = {
  selectedOwnersStatementIds: [],
  sortOption: OwnerStatementsSortOptions.NAME_ASC,
  invoiceSortOption: InvoiceTableSortOptions.INVOICE_DATE_DESC,
  ownerNameFilter: '',
  invoiceNameFilter: '',
  invoiceStatusFilter: new Set(),
  invoicePaymentStatusFilter: new Set(),
  invoiceCustomerFilter: new Set(),
  invoiceDateFilter: EMPTY_INVOICE_DATE_FILTER,
  invoiceGroupBy: null,
  selectedInvoicesIds: [],
};

const MyCompanyReducer = (state: MyCompanyState, action: Action): MyCompanyState => {
  switch (action.type) {
    case 'SET_SELECTED_OWNERS_STATEMENTS':
      return {
        ...state,
        selectedOwnersStatementIds: action.payload,
      };
    case 'SET_SELECTED_INVOICES':
      return {
        ...state,
        selectedInvoicesIds: action.payload,
      };
    case 'SET_SORT_OPTION':
      return {
        ...state,
        sortOption: action.payload,
      };
    case 'SET_INVOICE_SORT_OPTION':
      return {
        ...state,
        invoiceSortOption: action.payload,
      };
    case 'UPDATE_OWNER_NAME_FILTER':
      return {
        ...state,
        ownerNameFilter: action.payload,
      };
    case 'UPDATE_INVOICES_FILTER':
      return {
        ...state,
        invoiceNameFilter: action.payload,
      };
    case 'TOGGLE_INVOICE_STATUS_FILTER':
      return {
        ...state,
        invoiceStatusFilter: toggleInSet(state.invoiceStatusFilter, action.payload),
      };
    case 'TOGGLE_INVOICE_PAYMENT_STATUS_FILTER':
      return {
        ...state,
        invoicePaymentStatusFilter: toggleInSet(state.invoicePaymentStatusFilter, action.payload),
      };
    case 'TOGGLE_INVOICE_CUSTOMER_FILTER':
      return {
        ...state,
        invoiceCustomerFilter: toggleInSet(state.invoiceCustomerFilter, action.payload),
      };
    case 'SET_INVOICE_DATE_FILTER':
      return {
        ...state,
        invoiceDateFilter: action.payload,
      };
    case 'SET_INVOICE_GROUP_BY':
      return {
        ...state,
        invoiceGroupBy: action.payload,
      };
    case 'SET_INVOICE_FILTERS':
      return {
        ...state,
        invoiceStatusFilter: action.payload.statusFilter,
        invoicePaymentStatusFilter: action.payload.paymentStatusFilter,
        invoiceCustomerFilter: action.payload.customerFilter,
        invoiceDateFilter: action.payload.dateFilter,
        invoiceGroupBy: action.payload.groupBy,
      };
    case 'CLEAR_INVOICE_FILTERS':
      return {
        ...state,
        invoiceStatusFilter: new Set(),
        invoicePaymentStatusFilter: new Set(),
        invoiceCustomerFilter: new Set(),
        invoiceDateFilter: EMPTY_INVOICE_DATE_FILTER,
        invoiceGroupBy: null,
      };
    default:
      return state;
  }
};

const MyCompanyContext = React.createContext<MyCompanyContext | null>(null);

export const MyCompanyProvider = ({ children }: { children: React.ReactNode }) => {
  const [state, dispatch] = useReducer<Reducer<MyCompanyState, Action>>(MyCompanyReducer, initialState);

  const updateSelectedOwnersStatements = (selected: string[]) => {
    dispatch({
      type: 'SET_SELECTED_OWNERS_STATEMENTS',
      payload: selected,
    });
  };

  const updateSelectedInvoices = (selected: string[]) => {
    dispatch({
      type: 'SET_SELECTED_INVOICES',
      payload: selected,
    });
  };

  const updateSortOption = (sortOption: OwnerStatementsSortOptions) => {
    dispatch({
      type: 'SET_SORT_OPTION',
      payload: sortOption,
    });
  };

  const updateInvoiceSortOption = (sortOption: InvoiceTableSortOptions) => {
    dispatch({
      type: 'SET_INVOICE_SORT_OPTION',
      payload: sortOption,
    });
  };

  const updateOwnerNameFilter = (name: string) => {
    dispatch({
      type: 'UPDATE_OWNER_NAME_FILTER',
      payload: name,
    });
  };

  const updateInvoicesFilter = (name: string) => {
    dispatch({
      type: 'UPDATE_INVOICES_FILTER',
      payload: name,
    });
  };

  const toggleInvoiceStatusFilter = (status: InvoiceStatus) => {
    dispatch({
      type: 'TOGGLE_INVOICE_STATUS_FILTER',
      payload: status,
    });
  };

  const toggleInvoicePaymentStatusFilter = (paymentStatusId: InvoiceStatusId) => {
    dispatch({
      type: 'TOGGLE_INVOICE_PAYMENT_STATUS_FILTER',
      payload: paymentStatusId,
    });
  };

  const toggleInvoiceCustomerFilter = (ownerId: string) => {
    dispatch({
      type: 'TOGGLE_INVOICE_CUSTOMER_FILTER',
      payload: ownerId,
    });
  };

  const updateInvoiceDateFilter = (dateFilter: InvoiceDateFilter) => {
    dispatch({
      type: 'SET_INVOICE_DATE_FILTER',
      payload: dateFilter,
    });
  };

  const updateInvoiceGroupBy = (groupBy: InvoiceGroupByOption | null) => {
    dispatch({
      type: 'SET_INVOICE_GROUP_BY',
      payload: groupBy,
    });
  };

  const clearInvoiceFilters = () => dispatch({ type: 'CLEAR_INVOICE_FILTERS' });

  const applyInvoiceFilters = (filters: InvoiceFilterState) =>
    dispatch({ type: 'SET_INVOICE_FILTERS', payload: filters });

  const values = React.useMemo(
    () => ({
      ...state,
      updateSelectedOwnersStatements,
      updateSelectedInvoices,
      updateSortOption,
      updateOwnerNameFilter,
      updateInvoicesFilter,
      updateInvoiceSortOption,
      toggleInvoiceStatusFilter,
      toggleInvoicePaymentStatusFilter,
      toggleInvoiceCustomerFilter,
      updateInvoiceDateFilter,
      updateInvoiceGroupBy,
      clearInvoiceFilters,
      applyInvoiceFilters,
    }),
    [state]
  );

  return <MyCompanyContext.Provider value={values}>{children}</MyCompanyContext.Provider>;
};

export const useMyCompany = (): MyCompanyContext => {
  const context = React.useContext(MyCompanyContext);
  if (context === undefined) {
    throw new Error('`useMyCompany` hook must be used within a `MyCompanyProvider` component');
  }
  return context as MyCompanyContext;
};
